import { useCallback, useEffect, useRef, useState } from 'react'
import strings from '../locales/in-flight.json'
import { getTillInFlightOrders, submitPackComplete } from '../lib/api'
import { armSound, playBeep } from '../lib/beep'
import { arrivedAtFinalCheck, groupLines, kitchenDone, needsPack, newlyKitchenDone, shownInFlight } from '../lib/in-flight'
import { orderCode } from '../lib/order-code'
import { useSilentRefresh } from '../lib/useSilentRefresh'
import type { InFlightLine, InFlightOrder, KitchenTaskStatus } from '../types'
import { useLang } from '../lib/i18n'

type Key = keyof typeof strings

type RowState = { kind: 'idle' } | { kind: 'pending' } | { kind: 'failed' }

interface Notice {
  key: 'packRefused' | 'packQueued'
  code: string
}

// R537: one language at a time, under the till's switch (was English with the Arabic beside it).
function Pair({ k }: { k: Key }) {
  const lang = useLang()
  return <span lang={lang}>{strings[k][lang]}</span>
}

const KITCHEN_KEY: Record<KitchenTaskStatus, Key> = {
  PENDING: 'kitchenPending',
  IN_PROGRESS: 'kitchenInProgress',
  BLOCKED: 'kitchenBlocked',
  COMPLETED: 'kitchenDone',
  CANCELLED: 'kitchenCancelled',
}

const KITCHEN_CHIP: Record<KitchenTaskStatus, string> = {
  PENDING: 'bg-fleet-100 text-fleet-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-700',
  BLOCKED: 'bg-ember-100 text-ember-700',
  COMPLETED: 'bg-jade-100 text-jade-700',
  CANCELLED: 'bg-fleet-200 text-fleet-600',
}

function lineText(line: InFlightLine): string {
  const name = line.variantNameEn ? `${line.nameEn} \u2014 ${line.variantNameEn}` : line.nameEn
  return `${name} \u00d7 ${line.quantity}`
}

interface InFlightSectionProps {
  /** Called after a pack is accepted, so the Final check list re-reads and shows the order. */
  onPacked: () => void
  /** The Final check list as the screen last read it (null before its first read). */
  finalCheckOrders?: { id: string }[] | null
}

// R462 (Dee z#64729, "the till sees the whole order"): every order from confirmation until
// final check, its lines grouped Shelf and Kitchen with each kitchen line's state. When the
// last kitchen line turns ready the till beeps once (or shows a badge when the browser has not
// allowed sound yet). A mixed order then gets Packed: one POST pack-complete through apiSend,
// so offline it is queued once.
// R572 (b1): the section's read, its beep and its Packed tap are one hook, so the till's Orders
// face lists the same orders in flight with the same Packed action.
export function useInFlight({ onPacked, finalCheckOrders = null }: InFlightSectionProps) {
  const [orders, setOrders] = useState<InFlightOrder[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({})
  const [notice, setNotice] = useState<Notice | null>(null)
  const [soundOff, setSoundOff] = useState(false)
  const loading = useRef(false)
  const sending = useRef<Set<string>>(new Set())
  const seenCooking = useRef<Set<string>>(new Set())
  const rung = useRef<Set<string>>(new Set())

  // Autoplay rules: sound may start only after the person's first interaction with the page.
  useEffect(() => {
    const arm = () => {
      armSound()
      setSoundOff(false)
    }
    window.addEventListener('pointerdown', arm)
    window.addEventListener('keydown', arm)
    return () => {
      window.removeEventListener('pointerdown', arm)
      window.removeEventListener('keydown', arm)
    }
  }, [])

  const load = useCallback((silent: boolean = false) => {
    if (loading.current) return
    loading.current = true
    getTillInFlightOrders()
      .then((result) => {
        const list = Array.isArray(result) ? result : []
        if (newlyKitchenDone(list, seenCooking.current, rung.current).length > 0) {
          if (!playBeep()) setSoundOff(true)
        }
        setOrders(list)
        setLoadError(false)
      })
      .catch(() => {
        if (!silent) setLoadError(true)
      })
      .finally(() => {
        loading.current = false
      })
  }, [])

  // The Final check list below is read first: this section's first read waits one tick, so the
  // screen's own read is always the first request the tab makes (its tests pin that order).
  useEffect(() => {
    const first = setTimeout(() => load(), 0)
    return () => clearTimeout(first)
  }, [load])

  // Ruling 1: a kitchen-only order seen cooking here rings when it lands in the Final check list.
  useEffect(() => {
    if (!finalCheckOrders) return
    const ids = finalCheckOrders.map((o) => o.id)
    if (arrivedAtFinalCheck(ids, seenCooking.current, rung.current).length > 0) {
      if (!playBeep()) setSoundOff(true)
    }
  }, [finalCheckOrders])

  useSilentRefresh(() => {
    if (sending.current.size > 0) return
    load(true)
  })

  function setRow(id: string, state: RowState) {
    setRowStates((prev) => ({ ...prev, [id]: state }))
  }

  function leave(id: string) {
    setOrders((prev) => prev && prev.filter((o) => o.id !== id))
  }

  async function handlePacked(order: InFlightOrder) {
    if (sending.current.has(order.id)) return
    sending.current.add(order.id)
    setRow(order.id, { kind: 'pending' })
    let next: RowState = { kind: 'idle' }
    try {
      const outcome = await submitPackComplete(order.id)
      if (outcome.outcome === 'sent') {
        leave(order.id)
        onPacked()
      } else if (outcome.outcome === 'queued') {
        setNotice({ key: 'packQueued', code: orderCode(order) })
        leave(order.id)
      } else if (outcome.status === 400 || outcome.status === 404 || outcome.status === 409) {
        // A response arrived and refused: cancelled or moved on by HOP. The card leaves with
        // the reason; nothing is sent again.
        setNotice({ key: 'packRefused', code: orderCode(order) })
        leave(order.id)
      } else {
        next = { kind: 'failed' }
      }
    } catch {
      next = { kind: 'failed' }
    } finally {
      sending.current.delete(order.id)
      setRow(order.id, next)
    }
  }

  const shown = (orders ?? []).filter(shownInFlight)

  return { orders, loadError, load, rowStates, notice, soundOff, shown, handlePacked }
}

export type InFlight = ReturnType<typeof useInFlight>

// R572 (b1): the sound line and the notice, shown above the Orders face's list as well.
export function InFlightNotices({ fl }: { fl: InFlight }) {
  return (
    <>
      {fl.soundOff && (
        <p id="in-flight-sound-off" role="status" className="rounded-lg bg-amber-100 text-amber-700 text-sm font-semibold px-4 py-3">
          <Pair k="soundOff" />
        </p>
      )}

      {fl.notice && (
        <p id="in-flight-notice" role="status" className="rounded-lg bg-fleet-100 text-fleet-700 text-sm font-semibold px-4 py-3">
          {fl.notice.code}: <Pair k={fl.notice.key} />
        </p>
      )}
    </>
  )
}

// R572 (b1): the read's failed face and its retry, on the Orders face as well.
export function InFlightLoadError({ fl }: { fl: InFlight }) {
  if (!fl.loadError) return null
  return (
    <div className="space-y-3">
      <p id="in-flight-load-error" role="alert" className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3">
        <Pair k="loadFailed" />
      </p>
      <button
        id="in-flight-retry-btn"
        type="button"
        onClick={() => fl.load()}
        className="w-full min-h-tap rounded-lg border-2 border-fleet-300 text-fleet-700 font-semibold px-4 py-2 hover:bg-fleet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
      >
        <Pair k="retry" />
      </button>
    </div>
  )
}

// R572 (b1): one order in flight (code, tags, the person, its shelf and kitchen lines, a failed
// pack), the card body on the Orders face.
export function InFlightBody({ order, state }: { order: InFlightOrder; state: RowState }) {
  const { shelf, kitchen } = groupLines(order)
  const done = kitchenDone(order)
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold me-auto">{orderCode(order)}</span>
        {order.fulfillmentMethod === 'pickup' && (
          <span data-pickup-tag className="rounded-full bg-navy-tint text-navy text-xs font-semibold px-2 py-0.5">
            <Pair k="pickupTag" />
          </span>
        )}
        {done && (
          <span data-kitchen-done className="rounded-full bg-jade-100 text-jade-700 text-xs font-semibold px-2 py-0.5">
            <Pair k="kitchenDone" />
          </span>
        )}
      </div>
      {order.customerName && <p className="text-sm text-fleet-600">{order.customerName}</p>}

      {shelf.length > 0 && (
        <div data-group="shelf">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-fleet-500">
            <Pair k="shelfGroup" />
          </h3>
          <ul className="text-sm text-fleet-700 space-y-0.5">
            {shelf.map((line, idx) => (
              <li key={idx}>{lineText(line)}</li>
            ))}
          </ul>
        </div>
      )}

      <div data-group="kitchen">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-fleet-500">
          <Pair k="kitchenGroup" />
        </h3>
        <ul className="text-sm text-fleet-700 space-y-1">
          {kitchen.map((line, idx) => (
            <li key={idx} className="flex flex-wrap items-center gap-2">
              <span className="me-auto">{lineText(line)}</span>
              <span
                data-kitchen-state={line.kitchenTaskStatus ?? 'NONE'}
                className={`rounded-full text-xs font-semibold px-2 py-0.5 ${
                  line.kitchenTaskStatus ? KITCHEN_CHIP[line.kitchenTaskStatus] : 'bg-fleet-100 text-fleet-600'
                }`}
              >
                <Pair k={line.kitchenTaskStatus ? KITCHEN_KEY[line.kitchenTaskStatus] : 'kitchenNotStarted'} />
              </span>
            </li>
          ))}
        </ul>
      </div>

      {state.kind === 'failed' && (
        <p role="alert" className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3">
          <Pair k="packFailed" />
        </p>
      )}
    </>
  )
}

// R572 (b1): the one action of an order in flight, offered only where the server takes it.
export function PackedButton({ order, fl }: { order: InFlightOrder; fl: InFlight }) {
  if (!needsPack(order)) return null
  const pending = (fl.rowStates[order.id] ?? { kind: 'idle' }).kind === 'pending'
  return (
    <button
      type="button"
      data-packed-btn
      aria-disabled={pending}
      aria-busy={pending}
      onClick={() => void fl.handlePacked(order)}
      className={`w-full min-h-tap rounded-lg font-semibold px-4 py-2 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 ${
        pending ? 'bg-fleet-300 cursor-wait' : 'bg-navy hover:bg-fleet-700'
      }`}
    >
      <Pair k={pending ? 'packing' : 'packed'} />
    </button>
  )
}

export function InFlightSection({ onPacked, finalCheckOrders = null }: InFlightSectionProps) {
  const fl = useInFlight({ onPacked, finalCheckOrders })
  const { orders, loadError, rowStates, shown } = fl

  return (
    <section id="in-flight-section" aria-labelledby="in-flight-title" className="mb-8 space-y-3">
      <h2 id="in-flight-title" className="font-condensed text-lg font-bold uppercase tracking-wide">
        <Pair k="inFlightTitle" />
      </h2>

      <InFlightNotices fl={fl} />

      {orders === null && !loadError && (
        <p id="in-flight-loading" className="text-sm text-fleet-500">
          <Pair k="inFlightLoading" />
        </p>
      )}

      <InFlightLoadError fl={fl} />

      {!loadError && orders !== null && shown.length === 0 && (
        <p id="in-flight-empty" className="text-sm text-fleet-500">
          <Pair k="inFlightEmpty" />
        </p>
      )}

      {shown.length > 0 && (
        <ul id="in-flight-list" className="space-y-3">
          {shown.map((order) => {
            const state = rowStates[order.id] ?? { kind: 'idle' }
            const done = kitchenDone(order)
            return (
              <li
                key={order.id}
                data-flight-order-id={order.id}
                className={`rounded-lg border px-4 py-3 space-y-2 ${done ? 'border-jade-400 bg-jade-50' : 'border-fleet-300'}`}
              >
                <InFlightBody order={order} state={state} />

                <PackedButton order={order} fl={fl} />
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
